import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  DEFAULT_PRODUCTION_RATES,
  DEFAULT_VISITS_PER_YEAR,
  MODIFIER_MULTIPLIERS,
  RATE_UNIT_LABELS,
  SERVICE_LABELS,
} from './siteTakeoffConstants';

const DENSITY_SCALED_SERVICES = new Set(['pruning', 'trimming', 'deadheading']);

const MODIFIER_LABELS = {
  access_obstructed: 'Obstructed access',
  slope_sloped: 'Sloped terrain',
  litterExposure_high: 'High litter exposure',
  weedPressure_med: 'Medium weed pressure',
  weedPressure_high: 'High weed pressure',
  limitedAccess: 'Limited access',
  blowRestrictions: 'Blow restrictions',
};

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid #DBEAFE',
    background: '#F8FBFF',
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  title: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1D4ED8',
  },
  intro: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 1.45,
    marginBottom: 10,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
    marginBottom: 10,
  },
  summaryCard: {
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: '8px 10px',
  },
  summaryLabel: {
    fontSize: 10,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 3,
  },
  summaryValue: {
    fontSize: 14,
    fontWeight: 600,
    color: '#111827',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.45,
    marginBottom: 8,
  },
  noteList: {
    display: 'grid',
    gap: 6,
    marginBottom: 12,
  },
  note: {
    fontSize: 12,
    color: '#374151',
    lineHeight: 1.4,
  },
  serviceList: {
    display: 'grid',
    gap: 8,
    marginBottom: 12,
  },
  serviceCard: {
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 10,
  },
  serviceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  serviceName: {
    fontSize: 12,
    fontWeight: 600,
    color: '#111827',
  },
  badge: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
  },
  badgeDefault: {
    color: '#1F2937',
    background: '#E5E7EB',
  },
  badgeCustom: {
    color: '#1D4ED8',
    background: '#DBEAFE',
  },
  serviceMeta: {
    fontSize: 12,
    color: '#374151',
    marginBottom: 4,
  },
  serviceNote: {
    fontSize: 11,
    color: '#6B7280',
    lineHeight: 1.4,
  },
  modifierRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderTop: '1px solid #E5E7EB',
    fontSize: 12,
    color: '#374151',
  },
  modifierValue: {
    fontWeight: 600,
    color: '#111827',
  },
  empty: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 1.45,
  },
  toggle: {
    textTransform: 'none',
    fontSize: '0.75rem',
    minWidth: 0,
    padding: 0,
  },
}));

const formatRate = (value) => {
  const numeric = parseFloat(value) || 0;
  return numeric.toLocaleString('en-US', {
    maximumFractionDigits: numeric < 10 ? 1 : 0,
  });
};

const formatPercent = (value) => `${Math.round((value ?? 1) * 100)}%`;

const getQuantityNote = (service, unit) => {
  if (unit === 'lf_hr') {
    return 'Quantity comes from polygon perimeter or polyline length.';
  }
  if (unit === 'trees_hr') {
    return 'Quantity comes from the tree count entered on the area.';
  }
  if (unit === 'trees_or_sf_hr') {
    return 'Uses tree count when present. Otherwise it falls back to density-scaled shrub square footage.';
  }
  if (DENSITY_SCALED_SERVICES.has(service)) {
    return 'Uses mapped square footage, then scales it by shrub density before labor is calculated.';
  }
  return 'Quantity comes from mapped square footage.';
};

const getFrequencyNote = (serviceConfig, totalVisitsPerYear) => {
  const visitsPerYear = serviceConfig.visitsPerYear ?? 0;
  if (visitsPerYear >= totalVisitsPerYear) {
    return 'This is treated as a routine service and rolls into every-visit labor.';
  }
  return `This is treated as periodic labor and is applied ${visitsPerYear} times per year.`;
};

const ProductionRateExplainer = ({ areas = [], rateOverrides = {} }) => {
  const classes = useStyles();
  const [detailsOpen, setDetailsOpen] = useState(true);

  const usedServices = useMemo(() => {
    const used = new Set();
    (areas || []).forEach((area) => {
      (area.servicesRequired || []).forEach((service) => used.add(service));
    });
    return [...used];
  }, [areas]);

  const totalVisitsPerYear = rateOverrides._visitsPerYear || DEFAULT_VISITS_PER_YEAR;
  const effectiveServices = useMemo(
    () => usedServices.map((service) => {
      const defaults = DEFAULT_PRODUCTION_RATES[service];
      const overrides = rateOverrides[service] || {};
      if (!defaults) return null;
      const current = {
        ...defaults,
        rate: overrides.rate ?? defaults.rate,
        visitsPerYear: overrides.visitsPerYear ?? defaults.visitsPerYear,
        coveragePerVisit: overrides.coveragePerVisit ?? defaults.coveragePerVisit ?? 1,
      };
      return {
        service,
        defaults,
        current,
        hasOverride:
          overrides.rate !== undefined ||
          overrides.visitsPerYear !== undefined ||
          overrides.coveragePerVisit !== undefined,
      };
    }).filter(Boolean),
    [rateOverrides, usedServices]
  );

  const customServiceCount = effectiveServices.filter((item) => item.hasOverride).length;
  const customModifierCount = Object.keys(rateOverrides._modifiers || {}).length;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>How Production Rates Work</Typography>
        <Button
          color="primary"
          className={classes.toggle}
          onClick={() => setDetailsOpen((open) => !open)}
        >
          {detailsOpen ? 'Hide details' : 'Show details'}
        </Button>
      </div>

      <Typography className={classes.intro}>
        Rates are per manhour. The system converts mapped quantities into labor, then applies
        visit frequency, coverage, and site-condition modifiers.
      </Typography>

      <div className={classes.summaryGrid}>
        <div className={classes.summaryCard}>
          <div className={classes.summaryLabel}>Visits / Year</div>
          <div className={classes.summaryValue}>{totalVisitsPerYear}</div>
        </div>
        <div className={classes.summaryCard}>
          <div className={classes.summaryLabel}>Services In Use</div>
          <div className={classes.summaryValue}>{effectiveServices.length}</div>
        </div>
        <div className={classes.summaryCard}>
          <div className={classes.summaryLabel}>Custom Service Rates</div>
          <div className={classes.summaryValue}>{customServiceCount}</div>
        </div>
        <div className={classes.summaryCard}>
          <div className={classes.summaryLabel}>Custom Modifiers</div>
          <div className={classes.summaryValue}>{customModifierCount}</div>
        </div>
      </div>

      {detailsOpen && (
        <>
          <div className={classes.sectionTitle}>Core Assumptions</div>
          <div className={classes.noteList}>
            <div className={classes.note}>
              `Hours = quantity / rate x modifiers x coverage per visit`
            </div>
            <div className={classes.note}>
              Services with frequency below total visits per year are treated as periodic, not every-visit labor.
            </div>
            <div className={classes.note}>
              `Coverage` lets a service rotate work. Example: `50%` means only half of the full application is counted each visit.
            </div>
            <div className={classes.note}>
              Density-scaled plant work uses these default coverage assumptions: `none 5%`, `low 20%`, `med 45%`, `high 75%`.
            </div>
          </div>

          <Divider style={{ margin: '0 0 12px' }} />

          <div className={classes.sectionTitle}>Service Assumptions</div>
          {effectiveServices.length === 0 ? (
            <div className={classes.empty}>
              Draw areas and assign services to see service-specific assumptions here.
            </div>
          ) : (
            <div className={classes.serviceList}>
              {effectiveServices.map(({ service, defaults, current, hasOverride }) => (
                <div key={service} className={classes.serviceCard}>
                  <div className={classes.serviceHeader}>
                    <div className={classes.serviceName}>{SERVICE_LABELS[service] || defaults.label || service}</div>
                    <span className={`${classes.badge} ${hasOverride ? classes.badgeCustom : classes.badgeDefault}`}>
                      {hasOverride ? 'Custom' : 'Default'}
                    </span>
                  </div>
                  <div className={classes.serviceMeta}>
                    {formatRate(current.rate)} {RATE_UNIT_LABELS[current.unit] || current.unit} · {current.visitsPerYear}x/yr · {formatPercent(current.coveragePerVisit)} coverage
                  </div>
                  <div className={classes.serviceNote}>{getQuantityNote(service, current.unit)}</div>
                  <div className={classes.serviceNote}>{getFrequencyNote(current, totalVisitsPerYear)}</div>
                  {hasOverride && (
                    <div className={classes.serviceNote}>
                      Default: {formatRate(defaults.rate)} {RATE_UNIT_LABELS[defaults.unit] || defaults.unit} · {defaults.visitsPerYear}x/yr · {formatPercent(defaults.coveragePerVisit)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <Divider style={{ margin: '0 0 12px' }} />

          <div className={classes.sectionTitle}>Modifier Multipliers</div>
          <Box>
            {Object.entries(MODIFIER_MULTIPLIERS).map(([key, defaultValue]) => {
              const currentValue = rateOverrides._modifiers?.[key] ?? defaultValue;
              const isCustom = rateOverrides._modifiers?.[key] !== undefined;
              return (
                <div key={key} className={classes.modifierRow}>
                  <span>{MODIFIER_LABELS[key] || key}</span>
                  <span className={classes.modifierValue}>
                    {currentValue.toFixed(2)}x{isCustom ? ' custom' : ''}
                  </span>
                </div>
              );
            })}
          </Box>
        </>
      )}
    </div>
  );
};

export default React.memo(ProductionRateExplainer);
